import { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Package } from 'lucide-react';
import { useTranslation } from '../../context/TranslationContext';
import { translateWithFallback } from '../../utils/translateWithFallback';
import { getMyOrders } from '../../services/api.shop';
import { Order } from '../../types';
import { useProtectedQuery } from '../../hooks/useProtectedQuery';
import LoadingSpinner from '../LoadingSpinner';
import BackToHomeLink from '../BackToHomeLink';
import ProductSortDropdown from '../ProductSortDropdown';
import { formatMoney } from '../../utils/productValidation';
import { DEFAULT_PRODUCT_SORT, ProductSortKey } from '../../utils/productSort';
import { ORDER_SORT_OPTIONS, sortOrdersClient } from '../../utils/orderSort';
import {
    isActivePurchaseOrder,
    isHistoryPurchaseOrder,
    translateOrderStatus,
} from '../../utils/orderStatusLabel';
import {
    createHoldCountdownAnchor,
    formatCountdown,
    remainingFromAnchor,
} from '../../utils/reservationCountdown';

type ListTab = 'active' | 'history';

function statusBadgeClass(status: string, paymentNotCompleted?: boolean): string {
    if (paymentNotCompleted || status === 'Cancelled' || status === 'Refunded') {
        return 'bg-gray-200 text-gray-700';
    }
    if (status === 'Delivered' || status === 'Completed') return 'bg-emerald-100 text-emerald-800';
    if (status === 'Shipped' || status === 'InTransit') return 'bg-sky-100 text-sky-800';
    if (status === 'Paid' || status === 'Processing') return 'bg-green-100 text-green-800';
    if (status === 'Pending') return 'bg-amber-100 text-amber-900';
    return 'bg-gray-100 text-gray-700';
}

function formatDate(iso?: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

const OrderCard = ({
    o,
    tr,
}: {
    o: Order;
    tr: (k: string, fb: string) => string;
}) => {
    const thumb = o.product?.imageUrls?.[0] || o.primaryImageUrlSnapshot;
    const title = o.product?.title || o.productTitleSnapshot || `Product #${o.productId}`;
    const live = !!o.isLiveCheckout && String(o.status) === 'Pending';
    const paymentNotCompleted = !!o.isPaymentNotCompleted;
    const [nowMs, setNowMs] = useState(() => Date.now());

    useEffect(() => {
        if (!live) return;
        const id = window.setInterval(() => setNowMs(Date.now()), 1000);
        return () => window.clearInterval(id);
    }, [live]);

    // Anchor once per server payload (not every tick — recreating would freeze the timer)
    const holdAnchor = useMemo(
        () => createHoldCountdownAnchor(o.holdRemainingSeconds, o.reservedUntil),
        [o.holdRemainingSeconds, o.reservedUntil]
    );
    const left = remainingFromAnchor(holdAnchor, nowMs);
    const countdown = formatCountdown(left);

    return (
        <li className="overflow-hidden rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_55%)] bg-[color-mix(in_srgb,var(--bg-color),white_40%)] shadow-sm">
            <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:p-5">
                <div className="relative h-28 w-full shrink-0 overflow-hidden rounded-xl bg-[color-mix(in_srgb,var(--accent-color),transparent_80%)] sm:h-24 sm:w-24">
                    {thumb ? (
                        <img src={thumb} alt="" className="h-full w-full object-cover" />
                    ) : (
                        <div className="flex h-full w-full items-center justify-center text-[var(--text-color)] opacity-40">
                            <Package className="h-8 w-8" aria-hidden />
                        </div>
                    )}
                </div>

                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                            <p className="text-xs font-medium text-[color-mix(in_srgb,var(--text-color),transparent_45%)]">
                                #{o.id}
                                {o.orderDate ? ` · ${formatDate(o.orderDate)}` : ''}
                            </p>
                            <p className="mt-0.5 line-clamp-2 font-semibold text-[var(--text-color)]">{title}</p>
                            {live && left > 0 && (
                                <p className="mt-1 text-sm font-medium text-amber-900">
                                    {tr('order.checkout.payWithin', 'Pay within')} {countdown}
                                </p>
                            )}
                            {paymentNotCompleted && (
                                <p className="mt-1 text-sm text-[color-mix(in_srgb,var(--text-color),transparent_35%)]">
                                    {tr(
                                        'order.checkout.paymentNotCompleted.hint',
                                        'Payment was not completed in time. The item was released.'
                                    )}
                                </p>
                            )}
                        </div>
                        <span
                            className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${statusBadgeClass(
                                String(o.status),
                                paymentNotCompleted
                            )}`}
                        >
                            {translateOrderStatus(tr, String(o.status), {
                                isLiveCheckout: o.isLiveCheckout,
                                isPaymentNotCompleted: o.isPaymentNotCompleted,
                                paymentStatus: o.paymentStatus,
                            })}
                        </span>
                    </div>
                    <p className="mt-2 text-xl font-bold text-[var(--text-color)]">€{formatMoney(o.total)}</p>
                    <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                        {live && (
                            <Link
                                to={`/checkout/${o.productId}`}
                                className="inline-flex min-h-11 items-center justify-center rounded-xl bg-[var(--accent-color)] px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90"
                            >
                                {tr('order.checkout.finishPayment', 'Finish payment')}
                            </Link>
                        )}
                        <Link
                            to={`/orders/${o.id}`}
                            className={`inline-flex min-h-11 items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold transition ${
                                live
                                    ? 'border border-[color-mix(in_srgb,var(--accent-color),transparent_40%)] text-[var(--text-color)] hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_85%)]'
                                    : 'bg-[var(--accent-color)] text-white hover:opacity-90'
                            }`}
                        >
                            {tr('account.myorders.viewdetails', 'View details')}
                        </Link>
                        {o.trackingUrl && (
                            <a
                                href={o.trackingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex min-h-11 items-center justify-center gap-1.5 rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_40%)] px-4 py-2 text-sm font-medium text-[var(--text-color)] transition hover:bg-[color-mix(in_srgb,var(--accent-color),transparent_85%)]"
                            >
                                {tr('account.myorders.track', 'Track')}
                                <ExternalLink className="h-3.5 w-3.5 opacity-70" aria-hidden />
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </li>
    );
};

const AccountOrdersSection = () => {
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);
    const [sortKey, setSortKey] = useState<ProductSortKey>(DEFAULT_PRODUCT_SORT);
    const [tab, setTab] = useState<ListTab>('active');

    const {
        data: orders = [],
        isLoading,
        error,
    } = useProtectedQuery<Order[]>({
        queryKey: ['myOrders'],
        queryFn: getMyOrders,
    });

    const filtered = useMemo(() => {
        const list =
            tab === 'active'
                ? orders.filter(isActivePurchaseOrder)
                : orders.filter(isHistoryPurchaseOrder);
        return sortOrdersClient(list, sortKey);
    }, [orders, sortKey, tab]);

    if (isLoading) return <LoadingSpinner />;
    if (error) {
        return (
            <p className="rounded-lg bg-red-50 px-4 py-3 text-red-700">
                {t('account.hub.loaderror') || 'Unable to load orders.'}
            </p>
        );
    }

    const activeCount = orders.filter(isActivePurchaseOrder).length;
    const historyCount = orders.filter(isHistoryPurchaseOrder).length;

    return (
        <div className="mx-auto max-w-3xl py-4 sm:py-8">
            <BackToHomeLink />

            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div className="min-w-0">
                    <h1 className="text-2xl font-bold text-[var(--text-color)] sm:text-3xl">
                        {tr('account.myorders', 'My purchases')}
                    </h1>
                    <p className="mt-1 text-sm text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                        {tr('account.purchases.subtitle', 'Track orders and open details in one glance')}
                    </p>
                </div>
                {orders.length > 0 && (
                    <ProductSortDropdown
                        id="orders-sort"
                        value={sortKey}
                        onChange={setSortKey}
                        options={ORDER_SORT_OPTIONS}
                        ariaLabelKey="account.orders.sort.aria"
                        ariaLabelFallback="Sort purchases"
                    />
                )}
            </div>

            {orders.length > 0 && (
                <div
                    className="mb-4 flex gap-2 rounded-xl bg-[color-mix(in_srgb,var(--accent-color),transparent_88%)] p-1"
                    role="tablist"
                    aria-label={tr('account.orders.tabs.aria', 'Purchase list')}
                >
                    <button
                        type="button"
                        role="tab"
                        aria-selected={tab === 'active'}
                        onClick={() => setTab('active')}
                        className={`min-h-10 flex-1 rounded-lg px-3 text-sm font-semibold transition ${
                            tab === 'active'
                                ? 'bg-white text-[var(--text-color)] shadow-sm'
                                : 'text-[color-mix(in_srgb,var(--text-color),transparent_30%)]'
                        }`}
                    >
                        {tr('account.orders.tab.active', 'Active')}
                        {activeCount > 0 ? ` (${activeCount})` : ''}
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={tab === 'history'}
                        onClick={() => setTab('history')}
                        className={`min-h-10 flex-1 rounded-lg px-3 text-sm font-semibold transition ${
                            tab === 'history'
                                ? 'bg-white text-[var(--text-color)] shadow-sm'
                                : 'text-[color-mix(in_srgb,var(--text-color),transparent_30%)]'
                        }`}
                    >
                        {tr('account.orders.tab.history', 'History')}
                        {historyCount > 0 ? ` (${historyCount})` : ''}
                    </button>
                </div>
            )}

            {filtered.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] px-6 py-14 text-center">
                    <Package className="mx-auto mb-3 h-10 w-10 text-[var(--text-color)] opacity-35" aria-hidden />
                    <p className="text-[color-mix(in_srgb,var(--text-color),transparent_35%)]">
                        {tab === 'history'
                            ? tr('account.orders.history.empty', 'No past purchases yet.')
                            : orders.length > 0
                              ? tr('account.orders.active.empty', 'No open purchases.')
                              : tr('account.noorders', 'No purchases yet.')}
                    </p>
                    {tab === 'active' && (
                        <Link
                            to="/home"
                            className="mt-4 inline-flex text-sm font-semibold text-[var(--text-color)] underline"
                        >
                            {tr('account.purchases.browse', 'Browse the marketplace')}
                        </Link>
                    )}
                </div>
            ) : (
                <ul className="space-y-3">
                    {filtered.map((o) => (
                        <OrderCard key={o.id} o={o} tr={tr} />
                    ))}
                </ul>
            )}
        </div>
    );
};

export default AccountOrdersSection;
